import { useEffect, useState } from "preact/hooks";

// UX review (done-feedback): a just-tapped row stays where it was for a
// short "settling" window — checked and struck through — before
// `useDue.ts` moves it into "Done today". The completion itself is sent
// immediately at tap time (the window delays only the visual MOVE, never
// the save). `useDue` owns the timers; `TaskRow` only needs to know
// "is this row settling right now?" to render it as done in place.
//
// A tiny module-level store (same precedent as `useSnackbarPresence.ts`)
// rather than a prop threaded through `app.tsx` -> Today/My list ->
// `TaskList` -> `TaskRow`: there is exactly one `useDue` instance in the
// app, and the rows that read this are several layers away from it.

/** How long a tapped row stays in place, checked, before it moves. */
export const SETTLE_MS = 1500;

type Listener = () => void;

const settling = new Set<string>();
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function markSettling(taskId: string): void {
  if (settling.has(taskId)) return;
  settling.add(taskId);
  notify();
}

export function clearSettling(taskId: string): void {
  if (!settling.delete(taskId)) return;
  notify();
}

export function isSettling(taskId: string): boolean {
  return settling.has(taskId);
}

/** Drops every settling row at once — `useDue`'s unmount cleanup. */
export function clearAllSettling(): void {
  if (settling.size === 0) return;
  settling.clear();
  notify();
}

/** `true` while `taskId` is inside its post-tap settling window. */
export function useIsSettling(taskId: string): boolean {
  const [value, setValue] = useState(() => settling.has(taskId));

  useEffect(() => {
    setValue(settling.has(taskId));
    const listener: Listener = () => setValue(settling.has(taskId));
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [taskId]);

  return value;
}
